/**
 * uploadProgressStore - Zustand store for tracking XHR upload progress.
 *
 * Tracks the client-side upload phase for files being uploaded to GCS.
 * Each upload goes through states: queued -> uploading -> (cleared or failed)
 *
 * Design principles:
 * - Keyed by fileId for efficient lookup
 * - Stores File object for retry functionality
 * - Separate from SSE-based processing status (see fileStatusStore)
 *
 * Used by:
 * - Sidebar: Initiates uploads and tracks progress
 * - PhaseRing: Displays upload phase progress
 * - useBeforeUnload: Warns when uploads are active
 */

import { create } from 'zustand';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type UploadState = 'queued' | 'uploading' | 'uploaded' | 'failed';

export interface UploadProgress {
  bytesUploaded: number;
  bytesTotal: number;
}

interface UploadProgressState {
  /** Progress data keyed by fileId */
  uploads: Record<string, UploadProgress>;

  /** Upload state keyed by fileId */
  states: Record<string, UploadState>;

  /** Error messages keyed by fileId */
  errors: Record<string, string>;

  /** File objects keyed by fileId (for retry) */
  files: Record<string, File>;
}

interface UploadProgressActions {
  /** Queue a new upload (initial state before XHR starts) */
  queueUpload: (fileId: string, file: File) => void;

  /** Mark upload as started (XHR connection established) */
  startUpload: (fileId: string) => void;

  /** Update upload progress */
  setProgress: (fileId: string, progress: UploadProgress) => void;

  /** Mark upload as failed with error message */
  setFailed: (fileId: string, error: string) => void;

  /** Mark upload as completed (waiting for processing) */
  markUploaded: (fileId: string) => void;

  /** Clear upload tracking (on success or cancel) */
  clearUpload: (fileId: string) => void;

  /** Get stored file for retry */
  getFile: (fileId: string) => File | undefined;

  /** Check if any uploads are active */
  hasActiveUploads: () => boolean;

  /** Check if any uploads have failed */
  hasFailedUploads: () => boolean;

  /** Get all file IDs with active uploads (queued or uploading) */
  getActiveFileIds: () => string[];

  /** Clear all non-failed uploads (used on page load to clean orphaned state) */
  clearOrphanedUploads: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────────

export const useUploadProgressStore = create<UploadProgressState & UploadProgressActions>((set, get) => ({
  // Start with empty state - any previous upload state is stale since XHRs don't survive page refresh/HMR
  uploads: {},
  states: {},
  errors: {},
  files: {},

  queueUpload: (fileId, file) => set((state) => ({
    states: { ...state.states, [fileId]: 'queued' },
    files: { ...state.files, [fileId]: file },
    uploads: { ...state.uploads, [fileId]: { bytesUploaded: 0, bytesTotal: file.size } },
  })),

  startUpload: (fileId) => set((state) => ({
    states: { ...state.states, [fileId]: 'uploading' },
  })),

  setProgress: (fileId, progress) => set((state) => ({
    uploads: { ...state.uploads, [fileId]: progress },
    states: { ...state.states, [fileId]: 'uploading' },
  })),

  setFailed: (fileId, error) => set((state) => ({
    states: { ...state.states, [fileId]: 'failed' },
    errors: { ...state.errors, [fileId]: error },
  })),

  markUploaded: (fileId) => set((state) => ({
    states: { ...state.states, [fileId]: 'uploaded' },
  })),

  clearUpload: (fileId) => set((state) => {
    const { [fileId]: _u, ...uploads } = state.uploads;
    const { [fileId]: _s, ...states } = state.states;
    const { [fileId]: _e, ...errors } = state.errors;
    const { [fileId]: _f, ...files } = state.files;
    return { uploads, states, errors, files };
  }),

  getFile: (fileId) => get().files[fileId],

  hasActiveUploads: () => {
    const states = get().states;
    return Object.values(states).some(s => s === 'queued' || s === 'uploading');
  },

  hasFailedUploads: () => {
    const states = get().states;
    return Object.values(states).some(s => s === 'failed');
  },

  getActiveFileIds: () => {
    const states = get().states;
    return Object.entries(states)
      .filter(([_, state]) => state === 'queued' || state === 'uploading')
      .map(([fileId]) => fileId);
  },

  clearOrphanedUploads: () => {
    // Clear all uploads except failed ones (which user might want to retry)
    // This is called on app init to clean up state from previous page loads/HMR
    const states = get().states;
    const orphanedIds = Object.entries(states)
      .filter(([_, state]) => state !== 'failed')
      .map(([id]) => id);

    if (orphanedIds.length > 0) {
      console.log('[uploadProgressStore] Clearing orphaned uploads:', orphanedIds);
      orphanedIds.forEach(id => get().clearUpload(id));
    }
  },
}));

// ─────────────────────────────────────────────────────────────────────────────
// Selector hooks for efficient subscriptions
// ─────────────────────────────────────────────────────────────────────────────

export function useUploadProgress(fileId: string) {
  return useUploadProgressStore((state) => state.uploads[fileId]);
}

export function useUploadState(fileId: string) {
  return useUploadProgressStore((state) => state.states[fileId]);
}

export function useUploadError(fileId: string) {
  return useUploadProgressStore((state) => state.errors[fileId]);
}

export function useHasActiveUploads() {
  return useUploadProgressStore((state) =>
    Object.values(state.states).some(s => s === 'queued' || s === 'uploading')
  );
}

export function useHasFailedUploads() {
  return useUploadProgressStore((state) =>
    Object.values(state.states).some(s => s === 'failed')
  );
}
